import React, { useEffect, useState } from "react";
import SecondaryButton from "../buttons/SecondaryButton";
import Icon1 from "../icons/Icon";
import TextField2 from "../textfields/TextField2";
import PrimaryButton from "../buttons/PrimaryButton";
import MLTF1 from "../textfields/MLTF1";
import Space from "../layout/Space";
import {
  getArrayDocDataByRef,
  getDocDataByRef,
  getFlutterTime,
  getRefFromId,
  updateDocDataField,
} from "../models/DataFunctions";
import downloadPhotos from "../api/download/downloadPhotos";
import getTemporaryUrl from "../api/getTemporaryUrl";
import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken";
import { useAppContext } from "../config/AppContext";
import uploadPhoto from "../api/upload/uploadPhoto";
import { useSnackbar } from "notistack";
import validateDocument from "../api/folder/validateDocument";
import refuseFolder from "../api/folder/refuseFolder";
import createNewPhoto from "../api/create/createNewPhoto";
import deletePhoto from "../api/delete/deletePhoto";
import uploadPhotoNew from "../api/upload/uploadPhotoNew";
import SmallSpinner from "../loading/SmallSpinner";
import Modal1 from "../modals/Modal1";
import WarningButton from "../buttons/WarningButton";
import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi";
import { Player } from "@lottiefiles/react-lottie-player";
import loadingAnimation from "../animations/loading.json"; // Path to your JSON file
import createDocApi from "../api/database/createDocApi";
import { addActivityLog } from "../config/OnlineStatus";
import { isFolderAdmin } from "../pages/folder_pages/FolderHelpers";

const Photo = ({
  orgData,
  onClose,
  document,
  folder,
  userData,
  onFetch,

  validation = true,
}) => {
  const [photos, setPhotos] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [documentData, setDocumentData] = useState(null);

  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const [bigPhoto, setBigPhoto] = useState(null);

  useEffect(() => {
    handleFetchData();
  }, [document]);

  const handleFetchData = async () => {
    const token = await getOrRefreshFirebaseToken();

    const documentData = await getDocDataByRef(document.ref);
    setDocumentData(documentData);
    await getTemporaryUrl(token, "photo", document.id, folder.id, prefix);
    const photos_refs = documentData.photos || [];
    const photos = (await getArrayDocDataByRef(photos_refs)) || [];

    setPhotos(photos);
  };

  const handleDownload = async () => {
    await downloadPhotos(document.ref, folder.nom);
    handleClickVariant("Photos téléchargées avec succès", "success");
  };

  const handleDownloadSingle = async (photo) => {
    if (!photo.file?.url) return;

    try {
      const response = await fetch(photo.file.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = `${photo.title || 'photo'}.jpg`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
      handleClickVariant("Photo téléchargée avec succès", "success");
    } catch (error) {
      console.error("Erreur lors du téléchargement de la photo :", error);
      handleClickVariant("Erreur lors du téléchargement", "error");
    }
  };

  const handleOpenInNewWindow = (photo) => {
    if (!photo.file?.url) return;
    window.open(photo.file.url, '_blank');
  };
  const handleJoinPhotos = async () => {
    setLoadingData(true);
    const token = await getOrRefreshFirebaseToken();

    await uploadPhotoNew(folder.id, document.id, token, prefix);
    handleClickVariant("Photos jointes avec succès", "success");
    // Best-effort log: the guest portal has no org/user context (orgData /
    // userData are null there), and the photos are already joined — never let
    // a logging failure surface as an error to the user.
    try {
      await addActivityLog(
        "Photos jointes",
        folder.ref,
        orgData?.id,
        userData?.id,
        "photo_added",
        prefix
      );
    } catch (e) {
      console.warn("addActivityLog failed:", e);
    }

    await handleFetchData();
    setLoadingData(false);
  };
  const handleJoinLocalPhotos = async (id) => {
    setLoadingData2(id);
    const token = await getOrRefreshFirebaseToken();
    let photoref = getRefFromId("photo", id);
    await uploadPhoto(folder.id, document.id, token, id, prefix);
    if (isFolderAdmin(orgData, folder)) {
      await updateDocDataFieldApi(photoref, "state", "done", prefix);
    } else {
      await updateDocDataFieldApi(photoref, "state", "wait", prefix);
    }
    handleClickVariant("Photos jointes avec succès", "success");
    try {
      await addActivityLog(
        "Photo jointe " + photos.find((photo) => photo.id === id).title,
        folder.ref,
        orgData?.id,
        userData?.id,
        "photo_added",
        prefix
      );
    } catch (e) {
      console.warn("addActivityLog failed:", e);
    }

    await handleFetchData();
    onFetch();

    setLoadingData2(null);
  };
  const handleValidateLocal = async (photo, cancel = false) => {
    await updateDocDataFieldApi(
      photo.ref,
      "state",
      cancel ? "locked" : "done",
      prefix
    );
    handleClickVariant("Photo validée avec succès", "success");
    const photos = await getArrayDocDataByRef(document.photos || []);
    if (photos.every((photo) => photo.state === "done")) {
      await updateDocDataFieldApi(document.ref, "state", "done", prefix);
    }
    await addActivityLog(
      (cancel ? "Photo invalidée " : "Photo validée ") + photo.title,
      folder.ref,
      orgData.id,
      userData.id,
      "photo_validated",
      prefix
    );

    await handleFetchData();
  };

  const handleRefuseLocal = async (photo) => {
    await updateDocDataFieldApi(photo.ref, "state", "missing", prefix);
    await updateDocDataFieldApi(photo.ref, "file", null, prefix);
    handleClickVariant("Photo refusée avec succès", "success");
    await updateDocDataFieldApi(document.ref, "state", "missing", prefix);

    await addActivityLog(
      "Photo refusée " + photo.title,
      folder.ref,
      orgData.id,
      userData.id,
      "photo_refused",
      prefix
    );

    await handleFetchData();
  };

  const handleAddEmplacement = async () => {
    setLoadingData3(true);
    const token = await getOrRefreshFirebaseToken();
    await createNewPhoto(
      folder.id,
      token,
      title,
      description,
      document.id,
      prefix
    );
    handleClickVariant("Emplacement ajouté avec succès", "success");

    await addActivityLog(
      "Emplacement photo ajouté " + title,
      folder.ref,
      orgData.id,
      userData.id,
      "photo_slot_added",
      prefix
    );
    setTitle("");
    setDescription("");

    await handleFetchData();
    setLoadingData3(false);
  };

  const [loadingData, setLoadingData] = useState(false);
  const [loadingData2, setLoadingData2] = useState(null);
  const [loadingData3, setLoadingData3] = useState(false);
  const [loadingData4, setLoadingData4] = useState(false);
  const [openRefus, setOpenRefus] = useState(false);
  const [photo, setPhoto] = useState(null);

  const handleDeleteLocal = async (id) => {
    setLoadingData4(id);
    const token = await getOrRefreshFirebaseToken();
    await deletePhoto(document.id, id, token, folder.id, prefix);
    handleFetchData();
    handleClickVariant("Photo supprimée avec succès", "success");
    await addActivityLog(
      "Photo supprimée " + photos.find((photo) => photo.id === id).title,
      folder.ref,
      orgData.id,
      userData.id,
      "photo_deleted",
      prefix
    );
    setLoadingData4(null);
  };
  const handleValidateAll = async () => {
    const token = await getOrRefreshFirebaseToken();
    await validateDocument(document.id, folder.id, "doc", token, prefix);
    for (let photo of photos) {
      await updateDocDataFieldApi(photo.ref, "state", "done", prefix);
    }
    await addActivityLog(
      "Photos validées",
      folder.ref,
      orgData.id,
      userData.id,
      "photo_validated",
      prefix
    );
    onFetch();
    onClose();
  };
  const handleRefuseAll = async () => {
    const token = await getOrRefreshFirebaseToken();
    await refuseFolder(folder.id, document.id, token, "doc", prefix);
    for (let photo of photos) {
      await updateDocDataFieldApi(photo.ref, "state", "missing", prefix);
    }
    await addActivityLog(
      "Photos refusées",
      folder.ref,
      orgData.id,
      userData.id,
      "photo_refused",
      prefix
    );

    onFetch();
    onClose();
  };

  if (!document) {
    return <div>Chargement...</div>;
  }
  return (
    <div className="flex flex-row w-full h-full">
      <Modal1 isOpen={openRefus} onClose={() => setOpenRefus(false)}>
        <div className=" flex flex-col w-c400 bg-white rounded-xl p-4">
          <div className="text-h2">
            Voulez-vous vraiment effectuer cette action ?
          </div>
          <div className="text-h3 text-shadow_50 my-4">
            Cette action est irréversible. Le refus entraîne la suppression
            définitive des fichiers déposés.
          </div>

          <div className="flex flex-row justify-end gap-2 ">
            <SecondaryButton onClick={() => setOpenRefus(false)}>
              Annuler
            </SecondaryButton>
            <WarningButton
              onClick={() => {
                openRefus === "local"
                  ? handleRefuseLocal(photo)
                  : handleRefuseAll;
                setOpenRefus(false);
              }}
            >
              Confirmer
            </WarningButton>
          </div>
        </div>
      </Modal1>
      <Modal1 isOpen={bigPhoto} onClose={() => setBigPhoto(null)}>
        <div
          className="relative flex flex-col  bg-white rounded-xl items-center justify-center h-c700 cursor-pointer"
          onClick={() => setBigPhoto(null)}
        >
          <img
            src={bigPhoto}
            alt="photo"
            className="rounded-xl w-full h-full object-contain "
          />
          <div
            className="absolute right-0 p-2 rounded-l-lg bg-shadow_10 border-white border text-h3 text-shadow_50"
            onClick={(e) => {
              e.stopPropagation();
              let index = photos.findIndex(
                (photo) => photo.file?.url === bigPhoto
              );
              if (index < photos.length - 1) {
                setBigPhoto(photos[index + 1].file?.url);
              } else {
                setBigPhoto(photos[0].file?.url);
              }
            }}
          >
            <Icon1 icon_name="right" fill="white" size={25} />
          </div>
          <div
            className="absolute left-0 p-2 rounded-r-lg bg-shadow_10 border-white border text-h3 text-shadow_50"
            onClick={(e) => {
              e.stopPropagation();
              let index = photos.findIndex(
                (photo) => photo.file?.url === bigPhoto
              );
              if (index > 0) {
                setBigPhoto(photos[index - 1].file?.url);
              } else {
                setBigPhoto(photos[photos.length - 1].file?.url);
              }
            }}
          >
            <Icon1 icon_name="left" fill="white" size={25} />
          </div>
        </div>
      </Modal1>

      <div className="flex flex-row gap-2 w-full p-3 overflow-x-auto min-h-c400">
        {!photos && (
          <div className="w-full h-full flex justify-center items-center">
            <Player
              autoplay
              loop
              src={loadingAnimation}
              style={{ height: "100px", width: "200px" }}
            />
          </div>
        )}
        {photos?.map((photo, index) => (
          <div
            key={photo.id}
            className="flex flex-col gap-2 p-1 bg-shadow_3 rounded-xl w-c200 "
          >
            <div className="flex flex-row justify-between items-center px-2">
              <div className="text-h3">{photo.title}</div>
              {photo.file?.url && (
                <div className="flex flex-row gap-1">
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenInNewWindow(photo);
                    }}
                    className="cursor-pointer p-1 hover:bg-shadow_10 rounded-full transition-colors"
                    title="Ouvrir dans un nouvel onglet"
                  >
                    <Icon1 icon_name="open" fill="grey" height="18px" width="18px" />
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadSingle(photo);
                    }}
                    className="cursor-pointer p-1 hover:bg-shadow_10 rounded-full transition-colors"
                    title="Télécharger cette photo"
                  >
                    <Icon1 icon_name="download" fill="grey" height="18px" width="18px" />
                  </div>
                </div>
              )}
            </div>
            <div
              className=" w-c200 h-80 bg-shadow_5 rounded-xl cursor-pointer  "
              onClick={() =>
                photo.file?.url ? setBigPhoto(photo.file.url) : {}
              }
            >
              {photo.file && (
                <img
                  src={photo.file.url}
                  alt="photo"
                  className="rounded-xl w-full h-full object-contain "
                />
              )}
            </div>
            <div className="flex flew-row gap-2 w-full">
              <SecondaryButton
                disabled={
                  loadingData2 != null || loadingData || photo.state === "done"
                }
                className="w-fit"
                onClick={() => handleJoinLocalPhotos(photo.id)}
              >
                Joindre photo{" "}
                <SmallSpinner loading={loadingData2 === photo.id} />{" "}
              </SecondaryButton>
            </div>
            {validation && (
              <>
                {" "}
                {photo.state === "done" && (
                  <div className=" rounded-xl py-0.5 text-shadow_50 bg-green-200 px-2 text-h2 font-semibold w-fit">
                    Validée
                  </div>
                )}
                {photo.state === "wait" && (
                  <div className=" rounded-xl py-0.5 text-shadow_50 bg-orange-200 px-2 text-h2 font-semibold w-fit">
                    En attente de validation
                  </div>
                )}
                {photo.state === "missing" && (
                  <div className=" rounded-xl py-0.5 text-shadow_50 bg-red-200 px-2 text-h2 font-semibold w-fit">
                    Refusée
                  </div>
                )}
                {photo.description &&
                  photo.description !== "" &&
                  validation && (
                    <div className="text-h3 px-2 text-shadow_50 w-full">
                      {photo.description}
                    </div>
                  )}
              </>
            )}

            {isFolderAdmin(orgData, folder) && (
              <div className="flex flex-col flex-grow align-bottom justify-end border-1 rounded-md gap-1 ">
                <>
                  {validation && (
                    <>
                      <Space y={5} />{" "}
                      {photo.state !== "done" && (
                        <SecondaryButton
                          className="w-fit"
                          onClick={() => handleValidateLocal(photo)}
                        >
                          Valider
                        </SecondaryButton>
                      )}
                      {photo.state === "done" && (
                        <SecondaryButton
                          className="w-fit"
                          onClick={() => handleValidateLocal(photo, true)}
                        >
                          Annuler
                        </SecondaryButton>
                      )}
                      {photo.state !== "missing" && (
                        <SecondaryButton
                          className="w-fit"
                          onClick={() => {
                            setOpenRefus("local");
                            setPhoto(photo);
                          }}
                        >
                          Refuser
                        </SecondaryButton>
                      )}{" "}
                      <Space y={5} />
                    </>
                  )}

                  <SecondaryButton
                    className="w-fit"
                    onClick={() => handleDeleteLocal(photo.id)}
                  >
                    {" "}
                    <div className=" flex flex-row gap-2 items-center">
                      Supprimer
                      <SmallSpinner loading={loadingData4 === photo.id} />{" "}
                    </div>
                  </SecondaryButton>
                </>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-col rounded-r-xl bg-white shadow-md p-3 w-c300 gap-2">
        <div
          className="justify-end flex text-h3 text-shadow_50 cursor-pointer"
          onClick={onClose}
        >
          Fermer
        </div>
        <div className="text-h3 text-shadow_50 ">ACTIONS RAPIDES</div>

        <div
          onClick={handleDownload}
          className="flex flex-row gap-3 items-center align-middle justify-start w-full border-1 border-shadow_10 rounded-full px-2 py-0.5 hover:bg-shadow_4 cursor-pointer"
        >
          <Icon1 icon_name="download" fill="grey" height="18px" width="18px" />
          <div className="text-h3 text-black">Télécharger</div>
        </div>

        <div
          onClick={handleJoinPhotos}
          className="flex flex-row gap-3 items-center align-middle justify-start w-full border-1 border-shadow_10 rounded-full px-2 py-0.5 hover:bg-shadow_4 cursor-pointer"
        >
          <Icon1 icon_name="join" fill="grey" height="18px" width="18px" />
          <div className="text-h3  flex flex-row gap-2">
            Joindre photos <SmallSpinner loading={loadingData} />{" "}
          </div>
        </div>
        <Space y={10} />

        {isFolderAdmin(orgData, folder) && (
          <>
            {validation && photos && photos.length > 0 && (
              <>
                {" "}
                <div className="text-h3 text-shadow_50 ">VALIDATION</div>
                <div className="flex flex-row gap-3 items-center align-middle justify-start w-full border-1 border-shadow_10 rounded-full px-2 py-0.5 hover:bg-shadow_4 cursor-pointer">
                  <Icon1
                    icon_name="done"
                    fill="grey"
                    height="18px"
                    width="18px"
                  />
                  <div
                    className="text-h3 text-black"
                    onClick={handleValidateAll}
                  >
                    Valider tout
                  </div>
                </div>
                <div className="flex flex-row gap-3 items-center align-middle justify-start w-full border-1 border-shadow_10 rounded-full px-2 py-0.5 hover:bg-shadow_4 cursor-pointer">
                  <Icon1
                    icon_name="close"
                    fill="grey"
                    height="18px"
                    width="18px"
                  />
                  <div
                    className="text-h3 text-black"
                    onClick={() => {
                      setOpenRefus("all");
                    }}
                  >
                    Refuser tout
                  </div>
                </div>{" "}
                <Space y={10} />
              </>
            )}

            <div className="text-h3 text-shadow_50 ">NOUVEL EMPLACEMENT</div>
            <TextField2
              label="Titre Photo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            ></TextField2>
            <MLTF1
              label="Description Photo"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            ></MLTF1>
            <PrimaryButton className="w-fit" onClick={handleAddEmplacement}>
              <div className=" flex flex-row gap-2 items-center">
                Ajouter emplacement <SmallSpinner loading={loadingData3} />{" "}
              </div>
            </PrimaryButton>
          </>
        )}
      </div>
    </div>
  );
};

export default Photo;
